import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Collapse,
  CircularProgress,
  Backdrop,
} from '@material-ui/core';
import {
  Visibility as VisibilityIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardArrowRight,
} from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import ViewDocumentModal from './ViewDocumentModal';
import { digitizationApiKey, digitizationAWSDataApiUrl } from '../../../../../utils/config';

const styles = (theme) => ({
  // Base styling for status chips
  statusChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '4px',
    textAlign: 'center',
    minWidth: '80px',
  },
  // Success status chip color
  successChip: {
    backgroundColor: '#28a745',
    color: '#ffffff',
  },
  // Processed status chip color
  processedChip: {
    backgroundColor: '#28a745',
    color: '#ffffff',
  },
  // Failed status chip color
  failedChip: {
    backgroundColor: '#dc3545',
    color: '#ffffff',
  },
  // In progress status chip color
  inProgressChip: {
    backgroundColor: '#ff9800',
    color: '#ffffff',
  },
  // Unprocessed status chip color
  unprocessedChip: {
    backgroundColor: '#6c757d',
    color: '#ffffff',
  },
  // Table column width definitions
  expandCell: {
    width: '5%',
    minWidth: '5%',
  },
  fileNameCell: {
    width: '20%',
    minWidth: '20%',
  },
  poNumberCell: {
    width: '12%',
    minWidth: '12%',
  },
  batchNumberCell: {
    width: '12%',
    minWidth: '12%',
  },
  senderCell: {
    width: '15%',
    minWidth: '15%',
  },
  timestampCell: {
    width: '16%',
    minWidth: '16%',
  },
  statusCell: {
    width: '130px',
    minWidth: '130px',
    textTransform: 'capitalize'
  },
  processingStatusCell: {
    width: '12%',
    minWidth: '12%',
  },
  actionsCell: {
    width: '10%',
    minWidth: '10%',
  },
  // Styling for main document rows
  parentRow: {
    // backgroundColor: '#F9F9FC',
    '&:hover': {
      // backgroundColor: '#F0F0F0',
    },
  },
  // Styling for document split rows
  childRow: {
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#F8F9FA',
    },
  },
  // Styling for collapsible content cells
  collapseCell: {
    padding: 0,
  },
});

const DigitizationTable = ({ classes, data, loading, onRefresh, openRows, setOpenRows, filters }) => {
  // Modal state for viewing document details
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // State to store fetched splits data for each file
  const [splitsData, setSplitsData] = useState({});
  
  // State to track loading status for splits
  const [splitsLoading, setSplitsLoading] = useState({});

  // Clear splits cache when filters change to ensure fresh filtered data
  useEffect(() => {
    setSplitsData({});
  }, [filters]);


  // Toggles expansion of parent rows to show/hide document splits
  const handleRowClick = async (fileId, s3Key, senderEmail) => {
    const isCurrentlyOpen = openRows[fileId];
    
    if (isCurrentlyOpen) {
      // Close the currently open row and clear its cached splits data
      setOpenRows(prev => ({
        ...prev,
        [fileId]: false
      }));
      // Clear cached splits data for this file to ensure fresh data on next expansion
      setSplitsData(prev => {
        const updated = { ...prev };
        delete updated[fileId];
        return updated;
      });
    } else {
      // Close all others and open this one
      setOpenRows({
        [fileId]: true
      });
      
      // Fetch splits data if not already fetched
      if (!splitsData[fileId]) {
        setSplitsLoading(prev => ({ ...prev, [fileId]: true }));
        
        try {
          const payload = {
            fileId: fileId,
            s3Key: s3Key,
          };

          // Add status filter if processing_status criteria is selected
          if (filters?.criteria === 'processing_status' && filters?.searchKeyword) {
            payload.status = filters.searchKeyword;
          }
          
          const response = await axios.post(
            `${digitizationAWSDataApiUrl}/files/splits`,
            payload,
            {
              headers: {
                'x-api-key': digitizationApiKey
              }
            }
          );
          
          
          // Add parent's senderEmail to each split
          const splitsWithSender = (response.data.splits || []).map(split => ({
            ...split,
            senderEmail: senderEmail || split.senderEmail
          }));
          
          // Store the splits data with senderEmail
          setSplitsData(prev => ({
            ...prev,
            [fileId]: splitsWithSender
          }));
          
        } catch (error) {
          console.error('Error fetching splits:', error);
          setSplitsData(prev => ({
            ...prev,
            [fileId]: []
          }));
        } finally {
          setSplitsLoading(prev => ({ ...prev, [fileId]: false }));
        }
      }
    }
  };

  // Extracts the most recent timeline stage for parent documents
  const getLastTimelineStageForParent = (document) => {
    if (!document.timeline || document.timeline.length === 0) {
      return document.queueStage || 'N/A';
    }
    
    // Return the stage from the last timeline entry
    const lastTimeline = document.timeline[document.timeline.length - 1];
    return lastTimeline.stage || 'N/A';
  };

  // Extracts timeline stage and status for document splits
  const getLastTimelineStage = (split) => {
    if (!split.timeline || split.timeline.length === 0) {
      return {
        stage: split.queueStage || 'N/A',
        status: split.status || 'N/A'
      };
    }
    
    // Return stage from timeline but status from split top-level
    const lastTimeline = split.timeline[split.timeline.length - 1];
    return {
      stage: lastTimeline.stage,
      status: split.status || lastTimeline.status  // Use top-level status, fallback to timeline status
    };
  };

  // Generate status chips with appropriate colors for processing status
  const getStatusChip = (status) => {
    // Normalize status values to consistent display text
        const lowerStatus = status?.toLowerCase();
    let transformedStatus;
    
    switch(lowerStatus) {
      case 'processing':
      case 'queued':
      case 'in_progress':
        transformedStatus = 'In Progress';
        break;
      case 'processed':
      case 'success':
        transformedStatus = 'Success';
        break;
      case 'failed':
        transformedStatus = 'Failed';
        break;
      case 'unprocessed':
        transformedStatus = 'Unprocessed';
        break;
      default:
        transformedStatus = status || 'N/A';
    }
    
    if (!transformedStatus) return null;
    
    // Apply appropriate styling based on status type
    let chipClasses = [classes.statusChip];
    
    switch (transformedStatus.toLowerCase()) {
      case 'success':
        chipClasses.push(classes.processedChip);
        break;
      case 'failed':
        chipClasses.push(classes.failedChip);
        break;
      case 'in progress':
        chipClasses.push(classes.inProgressChip);
        break;
      case 'unprocessed':
        chipClasses.push(classes.unprocessedChip);
        break;
      case 'unprocessed':
        chipClasses.push(classes.unprocessedChip);
        break;
      default:
        chipClasses.push(classes.statusChip);
        break;
    }
    
    return <Chip label={transformedStatus} className={chipClasses.join(' ')} size="small" />;
  };

  // Process action button clicks for view and edit operations
  const handleActionClick = (action, item, event) => {
    event.stopPropagation();
    
    switch (action) {
      case 'view':
        setSelectedDocument(item);
        setViewModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Closes the document detail modal
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedDocument(null);
  };

  // Prepare data array from props (no fallback data)
  let documentsArray = [];
  
  if (data && Array.isArray(data)) {
    documentsArray = data;
  }

  return (
    <Grid item xs={12}>
      <TableContainer component={Paper}>
        <Table style={{ minWidth: 650, tableLayout: 'fixed' }}>
          <TableHead style={{ backgroundColor: "#F9F9FC" }}>
            <TableRow>
              <TableCell className={classes.expandCell}></TableCell>
              <TableCell className={classes.fileNameCell}>File Name</TableCell>
              <TableCell className={classes.poNumberCell}>PO Number</TableCell>
              <TableCell className={classes.batchNumberCell}>Batch Number</TableCell>
              <TableCell className={classes.senderCell}>Sender</TableCell>
              <TableCell className={classes.timestampCell}>Received Timestamp</TableCell>
              <TableCell className={classes.statusCell}>Status</TableCell>
              <TableCell className={classes.processingStatusCell}>Processing Status</TableCell>
              <TableCell align="center" className={classes.actionsCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    padding: '3rem',
                    flexDirection: 'column',
                    gap: '1rem'
                  }}>
                    {/* <CircularProgress size={40} thickness={4} /> */}
                    <Typography variant="body1" style={{ color: '#6c757d' }}>
                      Loading documents...
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : documentsArray.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                    <Typography variant="body1">
                      No documents found
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              documentsArray.map((fileData) => {
                const isExpanded = openRows[fileData.fileId];
                const isAnyRowExpanded = Object.values(openRows).some(open => open);
                const shouldHideRow = isAnyRowExpanded && !isExpanded;
                
                return (
                <React.Fragment key={fileData.fileId}>
                  {/* Parent Row - Main Document */}
                  <TableRow 
                    className={classes.parentRow}
                    style={{ 
                      display: shouldHideRow ? 'none' : 'table-row'
                    }}
                  >
                    <TableCell className={classes.expandCell}>
                      {/* Only show expand button if totalSplits > 0 */}
                      {fileData.totalSplits > 0 ? (
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleRowClick(fileData.fileId, fileData.s3Key, fileData.senderEmail)}
                        >
                          {openRows[fileData.fileId] ? (
                            <KeyboardArrowDown />
                          ) : (
                            <KeyboardArrowRight />
                          )}
                        </IconButton>
                      ) : null}
                    </TableCell>
                    <TableCell className={classes.fileNameCell}>
                      <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                        {fileData.originalFileName}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.poNumberCell}>
                      <Typography variant="body2">
                        {fileData.poNumber}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.batchNumberCell}>
                      <Typography variant="body2">
                        {fileData.batch}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.senderCell}>
                      <Typography variant="body2" style={{wordBreak: 'break-all' }} >
                        {fileData.senderEmail}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.timestampCell}>
                      <Typography variant="body2" >
                        {fileData.receivedAt ? new Date(fileData.receivedAt).toLocaleString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.statusCell}>{getStatusChip(fileData.status)}</TableCell>
                    <TableCell className={classes.processingStatusCell}>
                      <Typography variant="body2">
                        {fileData.totalSplits === 0 
                          ? getLastTimelineStageForParent(fileData)
                          : 'Document Split'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={classes.actionsCell}>
                      {/* .{fileData.totalSplits === 0 ? */}
                      <Tooltip title="View Details">
                        <IconButton 
                          style={{ 
                            padding: '4px', 
                            margin: '0 2px', 
                            color: '#6c757d',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                          onClick={(e) => handleActionClick('view', fileData, e)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> 
                      {/* : <></>} */}
                    </TableCell>
                  </TableRow>
                  
                  {/* Collapsible Child Rows - Document Splits */}
                  {/* Only show collapse section if totalSplits > 0 */}
                  {fileData.totalSplits > 0 && (
                    <TableRow>
                      <TableCell className={classes.collapseCell} colSpan={9}>
                        <Collapse
                          in={openRows[fileData.fileId]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {splitsLoading[fileData.fileId] ? (
                                <TableRow>
                                  <TableCell colSpan={9} align="center">
                                    <div style={{ padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                                      <CircularProgress size={24} />
                                      <Typography variant="body2" style={{ color: '#6c757d' }}>
                                        Loading splits data...
                                      </Typography>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ) : splitsData[fileData.fileId] && splitsData[fileData.fileId].length > 0 ? (
                                splitsData[fileData.fileId].map((split, index) => {
                                // Get the last timeline stage for this split
                                const lastStage = getLastTimelineStage(split);
                                
                                return (
                                <TableRow key={split.splitId} className={classes.childRow}>
                                  <TableCell className={classes.expandCell}></TableCell>
                                  <TableCell className={classes.fileNameCell}>
                                    <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                                      {split.s3Key ? split.s3Key.split('/').pop() : split.splitId}
                                    </Typography>
                                  </TableCell>
                                  <TableCell className={classes.poNumberCell}>
                                    <Typography variant="body2">
                                      {split.poNumber}
                                    </Typography>
                                  </TableCell>
                                  <TableCell className={classes.batchNumberCell}>
                                    <Typography variant="body2">
                                      {split.batch}
                                    </Typography>
                                  </TableCell>
                                  <TableCell className={classes.senderCell}>
                                    <Typography variant="body2" >
                                      {/* {split.docType || 'N/A'} */}
                                    </Typography>
                                  </TableCell>
                                  <TableCell className={classes.timestampCell}>
                                    <Typography variant="body2" >
                                      {/* {split.startTime ? new Date(split.startTime).toLocaleString() : 'N/A'} */}
                                    </Typography>
                                  </TableCell>
                                  <TableCell className={classes.statusCell}>{getStatusChip(lastStage.status)}</TableCell>
                                  <TableCell className={classes.processingStatusCell}>
                                    <Typography variant="body2" >
                                      {lastStage.stage}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" className={classes.actionsCell}>
                                    <Tooltip title="View Details">
                                      <IconButton 
                                        style={{ 
                                          padding: '4px', 
                                          margin: '0 2px', 
                                          color: '#6c757d',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px'
                                        }}
                                        onClick={(e) => handleActionClick('view', split, e)}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                                );
                              })
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={9} align="center">
                                    <div style={{ padding: '2rem' }}>
                                      <Typography variant="body2" style={{ color: '#6c757d' }}>
                                        No splits found
                                      </Typography>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* View Document Modal */}
      <ViewDocumentModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        document={selectedDocument}
      />
      
      {/* Main Loading Backdrop for Search/Pagination */}
      <Backdrop
        style={{
          zIndex: 9998,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}
        open={loading}
      >
        <CircularProgress color="primary" size={60} />
        <Typography variant="h6">Loading...</Typography>
      </Backdrop>
    </Grid>
  );
};

export default withStyles(styles)(DigitizationTable);